import { isCapacitor } from './isCapacitor'

let loadingTimeout = null
let isExternalBrowserOpen = false
let clearLoadingCallback = null

/**
 * Global loading state manager for external browser interactions
 * This bypasses HOC interference by managing loading state directly
 */
export const LoadingStateManager = {
  /**
   * Start loading state for external browser
   * @param {Function} clearLoadingCallback - Function to call to clear loading state
   */
  startExternalBrowserLoading: callback => {
    if (!isCapacitor) {
      return
    }

    console.log('Starting external browser loading state management')
    isExternalBrowserOpen = true
    clearLoadingCallback = callback

    // Clear any existing timeout
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
    }

    // Set up multiple fallback mechanisms to clear loading state

    // 1. Focus event listener (when user returns to app)
    const handleFocus = () => {
      if (isExternalBrowserOpen) {
        console.log('App focused - clearing external browser loading state')
        LoadingStateManager.clearExternalBrowserLoading()
      }
    }

    // 2. Visibility change listener (when app becomes visible)
    const handleVisibilityChange = () => {
      if (isExternalBrowserOpen && !document.hidden) {
        console.log(
          'App became visible - clearing external browser loading state'
        )
        LoadingStateManager.clearExternalBrowserLoading()
      }
    }

    // 3. Page show event (when page becomes visible)
    const handlePageShow = () => {
      if (isExternalBrowserOpen) {
        console.log('Page shown - clearing external browser loading state')
        LoadingStateManager.clearExternalBrowserLoading()
      }
    }

    // 4. Timeout fallback (3 seconds - shorter for better UX)
    loadingTimeout = setTimeout(() => {
      console.log('Timeout fallback - clearing external browser loading state')
      LoadingStateManager.clearExternalBrowserLoading()
    }, 3000)

    // Add event listeners
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)

    // Store cleanup function
    LoadingStateManager.cleanup = () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pageshow', handlePageShow)
      if (loadingTimeout) {
        clearTimeout(loadingTimeout)
        loadingTimeout = null
      }
    }
  },

  /**
   * Clear loading state for external browser
   * @param {Function} callback - Optional callback to call (if not provided, uses stored callback)
   */
  clearExternalBrowserLoading: (callback = null) => {
    if (!isExternalBrowserOpen) {
      return
    }

    console.log('Clearing external browser loading state')
    isExternalBrowserOpen = false

    // Clear timeout
    if (loadingTimeout) {
      clearTimeout(loadingTimeout)
      loadingTimeout = null
    }

    // Call the callback to clear Redux loading state
    const callbackToCall = callback || clearLoadingCallback
    if (callbackToCall) {
      try {
        callbackToCall()
        console.log('Loading state cleared successfully')
      } catch (error) {
        console.error('Error clearing loading state:', error)
      }
    }

    // Clean up event listeners
    if (LoadingStateManager.cleanup) {
      LoadingStateManager.cleanup()
    }

    // Reset stored callback
    clearLoadingCallback = null
  },

  /**
   * Force clear loading state (for error cases)
   */
  forceClearLoading: () => {
    console.log('Force clearing loading state')
    LoadingStateManager.clearExternalBrowserLoading()
  },

  /**
   * Manual clear for debugging (expose to window for console access)
   */
  manualClear: () => {
    console.log('Manual clear triggered')
    LoadingStateManager.clearExternalBrowserLoading()
  },

  /**
   * Check if external browser is currently open
   */
  isExternalBrowserOpen: () => {
    return isExternalBrowserOpen
  },

  /**
   * Get current loading state
   */
  getLoadingState: () => {
    return {
      isExternalBrowserOpen,
      hasTimeout: !!loadingTimeout,
      hasCallback: !!clearLoadingCallback,
    }
  },

  /**
   * Debug method to log current state
   */
  debug: () => {
    console.log('=== LoadingStateManager Debug ===')
    console.log('isExternalBrowserOpen:', isExternalBrowserOpen)
    console.log('hasTimeout:', !!loadingTimeout)
    console.log('hasCallback:', !!clearLoadingCallback)
    console.log('isCapacitor:', isCapacitor)
    console.log('document.hidden:', document.hidden)
    console.log('document.visibilityState:', document.visibilityState)
    console.log('================================')
  },
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.LoadingStateManager = LoadingStateManager
}
